import { DragEvent, ReactElement, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { HiOutlinePaperClip, HiOutlineXMark } from 'react-icons/hi2';
import Button from '../Button';
import {
  ALLOWED_ATTACHMENT_TYPES,
  FEEDBACK_TYPE_OPTIONS,
  MAX_ATTACHMENTS_PER_REPORT,
  PRODUCT_AREA,
  PRODUCT_AREA_OPTIONS,
} from '../../constants/feedback';
import { useSubmitFeedback } from '../../hooks/useFeedback';
import { buildFeedbackContext } from '../../utils/buildFeedbackContext';
import { apiErrorDetail } from '../../utils/apiError';
import { useAttachmentPicker } from './useAttachmentPicker';
import { inputClass } from '../formControlClasses';

const labelClass = 'cr-mb-1 cr-block cr-text-sm cr-font-semibold cr-text-blue-900';

interface FeedbackPanelProps {
  onClose: () => void;
}

/**
 * The report form. Mounted only while open, so the paste listener and the
 * form state both start clean each time it is opened.
 */
export default function FeedbackPanel({ onClose }: FeedbackPanelProps): ReactElement {
  const { pathname } = useLocation();
  const [feedbackType, setFeedbackType] = useState<number>(FEEDBACK_TYPE_OPTIONS[0].value);
  const [productArea, setProductArea] = useState<number>(PRODUCT_AREA.PORTAL);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { attachments, addFiles, remove, reset, rejection } = useAttachmentPicker();
  const submit = useSubmitFeedback();

  const typeHint = useMemo(
    () => FEEDBACK_TYPE_OPTIONS.find((option) => option.value === feedbackType)?.hint ?? '',
    [feedbackType],
  );

  const canSubmit = title.trim().length > 0 && body.trim().length > 0 && !submit.isPending;

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragging(false);
    addFiles(Array.from(event.dataTransfer.files ?? []));
  };

  const handleSubmit = (): void => {
    if (!canSubmit) return;
    submit.mutate(
      {
        report: {
          feedback_type: feedbackType,
          product_area: productArea,
          title: title.trim(),
          body: body.trim(),
          context: buildFeedbackContext(productArea, pathname),
        },
        files: attachments.map((item) => item.file),
      },
      {
        onSuccess: (result) => {
          if (result.failedAttachments.length) {
            // The report is saved either way — say which images did not
            // make it rather than implying the whole thing failed.
            toast.error(`Report sent, but these images did not upload: ${result.failedAttachments.join(', ')}`);
          } else {
            toast.success('Thanks — your report has been sent.');
          }
          reset();
          onClose();
        },
        onError: (error) => toast.error(`Could not send your report: ${apiErrorDetail(error)}`),
      },
    );
  };

  return (
    <div
      className="cr-flex cr-w-[min(420px,calc(100vw-2rem))] cr-flex-col cr-gap-3 cr-rounded-lg cr-border
        cr-border-cirrusBlue-100 cr-bg-white cr-p-4 cr-shadow-xl"
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      <div className="cr-flex cr-items-center cr-justify-between">
        <h3 className="cr-m-0 cr-text-lg cr-font-semibold cr-text-blue-900">Report an issue</h3>
        <button
          type="button"
          aria-label="Close feedback form"
          className="cr-cursor-pointer cr-border-0 cr-bg-transparent cr-text-blue-900"
          onClick={onClose}
        >
          <HiOutlineXMark size={20} />
        </button>
      </div>

      <p className="cr-m-0 cr--mt-1 cr-text-sm cr-text-blue-800">
        Add any suggestions or ideas you feel would improve the product for you, even if they appear difficult.
      </p>

      <div className="cr-flex cr-gap-2">
        <div className="cr-flex-1">
          <label className={labelClass} htmlFor="feedback-type">
            Type
          </label>
          <select
            id="feedback-type"
            className={inputClass}
            value={feedbackType}
            onChange={(event) => setFeedbackType(Number(event.target.value))}
          >
            {FEEDBACK_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="cr-flex-1">
          <label className={labelClass} htmlFor="feedback-area">
            Product area
          </label>
          <select
            id="feedback-area"
            className={inputClass}
            value={productArea}
            onChange={(event) => setProductArea(Number(event.target.value))}
          >
            {PRODUCT_AREA_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <p className="cr-m-0 cr-text-xs cr-italic cr-text-gray-600">{typeHint}</p>

      <div>
        <label className={labelClass} htmlFor="feedback-title">
          Summary
        </label>
        <input
          id="feedback-title"
          className={inputClass}
          value={title}
          maxLength={200}
          placeholder="One line — what went wrong?"
          onChange={(event) => setTitle(event.target.value)}
        />
      </div>

      <div>
        <label className={labelClass} htmlFor="feedback-body">
          Details
        </label>
        <textarea
          id="feedback-body"
          className={inputClass}
          rows={5}
          value={body}
          placeholder="What were you doing, what did you expect, what happened instead?"
          onChange={(event) => setBody(event.target.value)}
        />
      </div>

      <div
        className={`cr-rounded-md cr-border cr-border-dashed cr-p-2 cr-text-sm ${
          dragging ? 'cr-border-cirrusBlue-400 cr-bg-cirrusBlue-25' : 'cr-border-gray-350'
        }`}
      >
        <div className="cr-flex cr-items-center cr-justify-between">
          <span className="cr-text-gray-600">Paste a screenshot (Ctrl+V), or drop an image here</span>
          <button
            type="button"
            className="cr-flex cr-cursor-pointer cr-items-center cr-gap-1 cr-border-0 cr-bg-transparent
              cr-text-blue-900 cr-underline"
            onClick={() => fileInputRef.current?.click()}
          >
            <HiOutlinePaperClip size={16} /> Browse
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ALLOWED_ATTACHMENT_TYPES.join(',')}
            className="cr-hidden"
            onChange={(event) => {
              addFiles(Array.from(event.target.files ?? []));
              event.target.value = '';
            }}
          />
        </div>

        {attachments.length > 0 && (
          <div className="cr-mt-2 cr-flex cr-flex-wrap cr-gap-2">
            {attachments.map((item) => (
              <div key={item.key} className="cr-relative">
                <img
                  src={item.previewUrl}
                  alt={item.file.name}
                  className="cr-h-16 cr-w-16 cr-rounded cr-border cr-border-gray-350 cr-object-cover"
                />
                <button
                  type="button"
                  aria-label={`Remove ${item.file.name}`}
                  className="cr-absolute cr--right-2 cr--top-2 cr-cursor-pointer cr-rounded-full cr-border-0
                    cr-bg-red-100 cr-px-1 cr-text-red-800"
                  onClick={() => remove(item.key)}
                >
                  <HiOutlineXMark size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        {rejection && <p className="cr-mb-0 cr-mt-2 cr-text-xs cr-text-red-700">{rejection}</p>}
        <p className="cr-mb-0 cr-mt-1 cr-text-xs cr-text-gray-500">
          Up to {MAX_ATTACHMENTS_PER_REPORT} images. Where you are in the portal and any recent errors are attached
          automatically.
        </p>
      </div>

      <div className="cr-flex cr-justify-end cr-gap-2">
        <Button variant="ghost" size="compact" normalCase onClick={onClose}>
          Cancel
        </Button>
        <Button variant="outline" size="compact" normalCase disabled={!canSubmit} onClick={handleSubmit}>
          {submit.isPending ? 'Sending…' : 'Send report'}
        </Button>
      </div>
    </div>
  );
}
